import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { isEmpty, size } from 'lodash'
import { useTranslation } from 'react-i18next'
import { getUserRoles, SUPER_USER_ROLE } from 'services/auth/isUserAuthorized'

import { defaultTableConfigs } from 'pages/Administration/components/Table/configs'
import { instancesColumn } from './config'

import Table from 'pages/Administration/components/Table'
import WorkingTablesPanel from './WorkingTablesPanel'
import {
  onExportData,
  getSimpleNodeId,
  handleColumnDefs,
  sizeTableToFit,
  excelStyles
} from 'pages/Administration/helpers'
import {
  INSTANCE_TYPE,
  defaultActions,
  frameworkComponents
} from 'pages/Administration/constants'
import {
  handleOpenModal,
  getPermissionPayload,
  updatePermissionModal,
  resetPermissionModal,
  notifySuccess
} from 'pages/Administration/Tabs/Instances/helpers'
import {
  createInstanceApi,
  deleteInstance
} from 'services/api/administration/instances'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'
import { openModal } from 'components/Modal/actions'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { createNewInstance } from 'services/api/instances'
import { mapInstance } from 'src/App/utils'
import { instancesStore } from 'services/state'

const InstancesPanel = React.memo(({ admin, instanceType }) => {
  const { INSTANCE } = INSTANCE_TYPE
  const { instances, allInstances, roles } = admin
  const { t, i18n } = useTranslation([
    'Administration',
    'Common',
    'Document',
    'Shell'
  ])

  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  const [updateSelected, setUpdateSelected] = useState({})
  const [cellHasBeenClicked, setCellHasBeenClicked] = useState(false)
  const currentUser = useStoreSelector('user')
  const [newInstanceCreated, setNewInstanceCreated] = useState(false)
  const {
    getAllInstancesWithRole,
    updateInstancePermissions,
    updateInstancePermissionsBulk
  } = useMemo(() => createInstanceApi(instanceType), [instanceType])

  useEffect(async () => {
    await getAllInstancesWithRole()
  }, [newInstanceCreated])

  const handleAddInstance = () => {
    openModal('administrationInstance', {
      instanceType: instanceType,
      roles: roles,
      exsistingInstance: instances,
      allInstances: allInstances,
      gridApi: gridApi
    })
  }

  const onRowSelect = useCallback(
    (rowList) => {
      setSelectedRows(rowList)
    },
    [setSelectedRows]
  )

  // MODAL RESET
  const onResetModalSubmit = async () => {
    const payload = getResetPayload(selectedRows)

    try {
      await updateInstancePermissionsBulk(payload)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('ResetAll', t)
    } catch (err) {
      console.error(err)
    }
  }

  const onResetSecondaryModalSubmit = async () => {
    const payload = getResetPayload(selectedRows)

    try {
      await updateInstancePermissions(payload)

      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Reset', t)
    } catch (err) {
      console.error(err)
    }
  }

  const handleResetAllPermissions = () => {
    resetPermissionModal(onResetModalSubmit, onResetSecondaryModalSubmit, t)
  }

  // MODAL CLONING

  const handleCloningInstance = () => {
    openModal('administrationInstanceClone', {
      instanceType: instanceType,
      roles: roles,
      allInstances: allInstances,
      selectedRows: selectedRows,
      gridApi: gridApi
    })
  }

  // MODAL UPDATE
  const onUpdateModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateInstancePermissionsBulk(payload)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('UpdateAll', t)
    } catch (err) {
      console.error(err)
    }
  }

  const onUpdateSecondaryModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateInstancePermissions(payload)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Update', t)
    } catch (err) {
      console.error(err)
    }
  }

  function getResetPayload(element) {
    const permissionPayload = {
      read: false,
      import_: false,
      export: false,
      create: false,
      update: false,
      delete: false
    }

    const payload = {
      instanceId: element[0].ubliqueId,
      permissions: [
        { permission: permissionPayload, roleName: element[0].roleName }
      ]
    }
    return payload
  }

  const handleCreateNewInstance = () => {
    openModal('createNewInstance', {
      instances: allInstances,
      onSubmit: async (name) => {
        const newInstance = await createNewInstance(name)
        if (newInstance !== null) {
          setNewInstanceCreated(!newInstanceCreated)
          notifySuccess('Create', t)
          const mappedInstance = mapInstance(newInstance)
          instancesStore.update((prev) => {
            prev.push(mappedInstance)
            return prev
          })
        }
      }
    })
  }

  function getUpdatePayload(element) {
    const permissionPayload = getPermissionPayload(element)
    const payload = {
      instanceId: element.idInstance,
      permissions: [
        { permission: permissionPayload, roleName: element.roleName }
      ]
    }
    return payload
  }

  const headerActions = useMemo(() => {
    if (!isEmpty(gridApi)) {
      const actions = {
        selected: [
          {
            id: 'clone',
            title: t('Administration:Clone'),
            handleClick: handleCloningInstance
          },
          {
            id: 'reset',
            title: t('Common:ResetPermissions'),
            handleClick: handleResetAllPermissions
          },
          {
            id: 'deleteInstance',
            title: t('Administration:RolesManagement.Actions.Delete'),
            handleClick: () => {
              onDeleteInstance()
            }
          }
        ],
        empty: [
          {
            id: 'exportRole',
            title: t('Administration:RolesManagement.Actions.ExportAll'),
            handleClick: () => {
              gridApi &&
                columnApi &&
                onExportData(
                  gridApi,
                  columnApi,
                  instanceType === INSTANCE_TYPE.INSTANCE
                    ? t('RolesManagement.InstanceTitle')
                    : t(`ScenarioManagement.Title`)
                )
            }
          },
          {
            id: 'addRole',
            title: t('Administration:RolesManagement.Actions.InsertNewRole'),
            handleClick: handleAddInstance
          }
        ]
      }

      const isSuperuser = getUserRoles(currentUser)?.includes(SUPER_USER_ROLE)
      if (instanceType === INSTANCE && isSuperuser) {
        const createAction = {
          id: 'createNewInstance',
          title: t('Shell:NewInstance.NewInstance'),
          handleClick: handleCreateNewInstance
        }
        actions.empty.push(createAction)
        actions.selected.push(createAction)
      }

      return actions
    }
    return defaultActions
  }, [t, gridApi, handleResetAllPermissions, onExportData, handleAddInstance])

  // MODAL DELETION
  const onDeleteInstance = () => {
    openModal('confirmAction', {
      modalHeading: t('Administration:RolesManagement.DeleteInstance'),
      message: t('Common:InstanceDeletionConfirmation'),
      onConfirm: onDeleteInstanceSubmit,
      loadingOverlay: true
    })
  }

  const { t: agGridT, destroyed } = useAgGridTranslation()

  const onDeleteInstanceSubmit = async () => {
    // setIsLoading(true)

    const deletionErrors = []
    for (const element of gridApi?.getSelectedNodes()) {
      try {
        const deletionPayload = {
          instance: element.data.ubliqueId,
          roleToDelete: element.data.roleName
        }
        await deleteInstance(deletionPayload, instanceType)

        gridApi.deselectAll()
        setSelectedRows([])
        await getAllInstancesWithRole()
        notifySuccess('Delete', t)
      } catch (err) {
        deletionErrors.push(err.message) // Is it still necessary?
      }
    }

    // setIsLoading(false)
  }

  useEffect(() => {
    if (cellHasBeenClicked) {
      updatePermissionModal(
        onUpdateModalSubmit,
        onUpdateSecondaryModalSubmit,
        t
      )
      setCellHasBeenClicked(false)
    }
  }, [cellHasBeenClicked])

  return (
    <Fragment>
      <div>
        {/*<h2 className='no-action-view'>{t('RolesManagement.InstanceTitle')}</h2>*/}
        <Table
          headerActions={headerActions}
          columnDefs={handleColumnDefs(instancesColumn, t)}
          rowData={size(instances) ? instances : []}
          getRowNodeId={getSimpleNodeId}
          onCellClicked={(e) => {
            setUpdateSelected({
              instanceName: e.data.title,
              idInstance: e.data.ubliqueId,
              roleName: e.data.roleName,
              column: e.colDef.colId,
              oldPermission: e.data.permission
            })

            if (handleOpenModal(e)) setCellHasBeenClicked(true)
          }}
          onGridReady={({ api, columnApi }) => {
            setGridApi(api)
            setColumnApi(columnApi)
            sizeTableToFit(api)
          }}
          key={i18n.language}
          localeTextFunc={agGridT}
          onSelectionChanged={onRowSelect}
          frameworkComponents={frameworkComponents}
          excelStyles={excelStyles}
          defaultTableConfigs={defaultTableConfigs}
        />
        {selectedRows.length === 1 && (
          <div className='role-view' key={selectedRows[0]}>
            <div className='children-view no-margin'>
              <WorkingTablesPanel
                admin={admin}
                instanceType={instanceType}
                instanceSelected={selectedRows[0]}
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
})
export default React.memo(InstancesPanel)
